import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { fetchWithAuth, fetchWithAuthUpload } from "../../Context/UserContext";
import PageLayout from "../../components/layout/PageLayout";
import { LoadingOverlay } from "../../components/ui/loading-overlay";
import API_URL from "../../config";
import { toast } from "sonner";
import { FileText } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";

const DetallePostulacion = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [postulacion, setPostulacion] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (applicationId) {
      fetchPostulacion(applicationId);
    }
  }, [applicationId]);

  const fetchPostulacion = async (applicationId) => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/licit/get_application_detail/${applicationId}`
      );
      if (response.ok) {
        const data = await response.json();

        let respuestasProcesadas = {
          ...data.application.respuestas_preguntas_licitador,
        };

        // Si la respuesta es "", asignamos el archivo correspondiente
        Object.entries(data.application.archivos_respuestas || {}).forEach(
          ([idPregunta, archivo]) => {
            if (respuestasProcesadas[idPregunta] === "") {
              respuestasProcesadas[idPregunta] = {
                file_name: archivo.file_name,
                file_path: archivo.file_path,
                id_file: archivo.id_file,
              };
            }
          }
        );

        setPostulacion({
          ...data,
          application: {
            ...data.application,
            respuestas_preguntas_licitador: respuestasProcesadas,
          },
        });
      } else {
        console.error("Error al obtener la postulación");
      }
    } catch (error) {
      console.error("Error en la solicitud:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEliminarPostulacion = async () => {
    if (
      !window.confirm("¿Estás seguro de que deseas eliminar esta postulación?")
    )
      return;
    setIsLoading(true);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/licit/delete_application/${applicationId}`,
        {
          method: "DELETE",
        }
      );

      if (response.ok) {
        toast.success("Postulación eliminada exitosamente.");
        navigate("/mis-postulaciones");
      } else {
        toast.error("Error al eliminar la postulación.");
      }
    } catch (error) {
      console.error("Error eliminando la postulación:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadFile = async (fileId) => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuthUpload(
        `${API_URL}/upload/download-file/${fileId}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        throw new Error("Error al obtener el archivo");
      }

      const data = await response.json();

      window.open(data.file_url, "_blank");
    } catch (error) {
      console.error("Error al descargar archivo:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageLayout>
      {isLoading ? (
        <LoadingOverlay />
      ) : (
        postulacion && (
          <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
            <Card>
              <CardContent className="flex flex-col gap-6 p-6 sm:p-8">
                <h3 className="text-center text-2xl font-semibold">
                  Detalle de la Postulación
                </h3>

                <section className="flex flex-col gap-2 border-t border-border pt-4">
                  <h4 className="font-semibold text-primary">
                    Información de la Licitación
                  </h4>
                  <p>
                    <strong>Título de la licitación:</strong>{" "}
                    <Link
                      to={`/ver-licitacion/${
                        postulacion.tender.id_tender
                      }/${postulacion.tender.titulo
                        .toLowerCase()
                        .replace(/\s+/g, "-")}`}
                      className="text-primary underline-offset-2 hover:underline"
                    >
                      {postulacion.tender.titulo}
                    </Link>
                  </p>

                  <p>
                    <strong>ID de la Licitación:</strong>{" "}
                    {postulacion.tender.id_tender}
                  </p>
                  {postulacion.tender.company_name &&
                    postulacion.tender.company_name !== "No asignada" && (
                      <p>
                        <strong>Empresa:</strong>{" "}
                        <a
                          href={`/company-profile/${
                            postulacion.tender.id_company
                          }/${encodeURIComponent(postulacion.tender.company_name)}`}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {postulacion.tender.company_name.length > 25
                            ? `${postulacion.tender.company_name.slice(0, 25)}...`
                            : postulacion.tender.company_name}
                        </a>
                      </p>
                    )}

                  {postulacion.tender.user_name && (
                    <p>
                      <strong>Usuario que la publicó:</strong>{" "}
                      <a
                        href={`/profile/${
                          postulacion.tender.id_user
                        }/${encodeURIComponent(postulacion.tender.user_name)}`}
                        className="text-primary underline-offset-2 hover:underline"
                      >
                        {postulacion.tender.user_name}
                      </a>
                    </p>
                  )}

                  <p>
                    <strong>Categoría:</strong> {postulacion.tender.categoria}
                  </p>
                  <p>
                    <strong>Ciudades del Servicio:</strong>{" "}
                    {postulacion.tender.ciudad
                      ? JSON.parse(postulacion.tender.ciudad).join(", ")
                      : "No especificado"}
                  </p>
                </section>

                <section className="flex flex-col gap-2 border-t border-border pt-4">
                  <h4 className="font-semibold text-primary">Oferta Realizada</h4>
                  <p>
                    <strong>Id de la postulación:</strong>{" "}
                    {postulacion.application.id_application}
                  </p>
                  <p>
                    <strong>Fecha de la postulación:</strong>{" "}
                    {postulacion.application.created_at}
                  </p>
                  <p>
                    <strong>Detalles:</strong>{" "}
                    {postulacion.application.detalles_oferta}
                  </p>

                  {postulacion.application.archivos_adjuntos &&
                    postulacion.application.archivos_adjuntos.length > 0 && (
                      <div className="mt-2 flex flex-col gap-2">
                        <h4 className="font-semibold text-primary">
                          Documentos Adjuntos
                        </h4>
                        <ul className="flex flex-col gap-2">
                          {postulacion.application.archivos_adjuntos.map(
                            (archivo, index) => (
                              <li key={index}>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="gap-2"
                                  onClick={() => downloadFile(archivo.id_file)}
                                  title={archivo.file_name}
                                >
                                  <FileText className="h-4 w-4" />
                                  {archivo.file_name.length > 30
                                    ? `${archivo.file_name.slice(0, 27)}...`
                                    : archivo.file_name}
                                </Button>
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    )}
                </section>

                <section className="flex flex-col gap-2 border-t border-border pt-4">
                  <h4 className="font-semibold text-primary">
                    Preguntas y Respuestas
                  </h4>
                  {Object.entries(
                    postulacion.application.respuestas_preguntas_licitador || {}
                  ).length > 0 ? (
                    <ul className="flex flex-col gap-3">
                      {Object.entries(
                        postulacion.application.respuestas_preguntas_licitador
                      ).map(([pregunta, respuesta], index) => (
                        <li key={index}>
                          <p>
                            <strong>
                              {index + 1}. {pregunta}
                            </strong>
                          </p>
                          <p className="mt-1">
                            {typeof respuesta === "object" &&
                            respuesta?.file_name ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-2"
                                onClick={() => downloadFile(respuesta.id_file)}
                                title={respuesta.file_name}
                              >
                                <FileText className="h-4 w-4" />
                                {respuesta.file_name.length > 20
                                  ? `${respuesta.file_name.slice(0, 17)}...`
                                  : respuesta.file_name}
                              </Button>
                            ) : (
                              respuesta || "No respondida"
                            )}
                          </p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No hay respuestas registradas.</p>
                  )}
                </section>

                <section className="flex justify-center border-t border-border pt-4">
                  <Button variant="destructive" onClick={handleEliminarPostulacion}>
                    Eliminar Postulación
                  </Button>
                </section>
              </CardContent>
            </Card>
          </div>
        )
      )}
    </PageLayout>
  );
};

export default DetallePostulacion;
